/**
 * Drink Menus — search what accounts pour, and see which slots name nobody.
 *
 * The search is the feature; everything else is context for a result. Type a
 * drink, an ingredient, a company or a city in the param bar and the page
 * narrows to it.
 *
 * **An unbranded line is the opportunity, not a gap in the data.** Four fifths
 * of juice and lemonade name no brand, and that is exactly what an account
 * pouring our product through a distributor's label looks like from the
 * street. So brandless lines are never hidden by default, and the page marks
 * them rather than dimming them.
 *
 * Every count is a count of accounts we could *read* — chain menus hide behind
 * location pickers, so the capture leans toward smaller operators.
 *
 * **Each account shows its last good capture, so the date is per account** and
 * is printed on each one. An account whose newest fetch failed is tagged
 * *stale* — on its chip and on every drink row — rather than hidden.
 *
 * Nothing is computed here; every figure arrives in the payload.
 */

import { useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { type ExportFormat, exportDataset } from '../shared/exportDataset'
import { count, dataset, text } from '../shared/rows'

import '../report.css'

const NONE = '—'

/** Rows rendered per step. The payload carries every matching line; this only
 * keeps the table from mounting thousands of rows at once. */
const PAGE = 200

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function whole(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

function pct(value: number | null): string {
  return value === null ? NONE : `${value.toFixed(1)}%`
}

/** Why an account is stale, for its tooltip. */
function staleNote(row: Record<string, Cell>): string {
  return `Latest fetch on ${text(row['last_attempt_on']) ?? 'an unknown date'} failed; this menu is from ${text(row['captured_on']) ?? 'an earlier capture'}.`
}

function StaleTag({ row }: { row: Record<string, Cell> }) {
  return (
    <span className="dm-tag is-stale" title={staleNote(row)}>
      stale
    </span>
  )
}

/**
 * Export the drinks the filters selected -- "all lemonades", "anything with
 * guava", only Subway -- as CSV or Excel.
 *
 * The file is the server's `results` dataset under this run's own params, so
 * it carries every matching line however few the table has paged in, with the
 * capture date and the stale flag on each row. The company is one of those
 * params (an account chip sets it), so the file is always what the table shows.
 */
function ExportBar({
  slug,
  params,
  lines,
}: {
  slug: string
  params: Record<string, unknown>
  lines: number
}) {
  const [busy, setBusy] = useState<ExportFormat | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const run = (format: ExportFormat) => {
    setBusy(format)
    setFailed(null)
    exportDataset(slug, params, 'results', format)
      .catch((error: unknown) => {
        setFailed(error instanceof Error ? error.message : String(error))
      })
      .finally(() => {
        setBusy(null)
      })
  }
  return (
    <div className="dm-export" role="group" aria-label="Export these drinks">
      <span className="dm-export-label">
        Export these {whole(lines)} lines
      </span>
      <button
        type="button"
        className="dm-chip"
        disabled={busy !== null || lines === 0}
        onClick={() => {
          run('xlsx')
        }}
      >
        {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
      </button>
      <button
        type="button"
        className="dm-chip"
        disabled={busy !== null || lines === 0}
        onClick={() => {
          run('csv')
        }}
      >
        {busy === 'csv' ? 'Preparing…' : 'CSV'}
      </button>
      {failed !== null && (
        <span className="dm-export-error" role="alert">
          {failed}
        </span>
      )}
    </div>
  )
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <article className="dm-kpi">
      <p className="dm-kpi-label">{label}</p>
      <p className="dm-kpi-value cell-number">{value}</p>
      {detail !== undefined && <p className="dm-kpi-detail">{detail}</p>}
    </article>
  )
}

const ALL = '(all)'

export function DrinkMenus({ result, values, setParam }: CustomReportProps) {
  const headline = dataset(result, 'headline')[0]
  const results = dataset(result, 'results')
  const accounts = dataset(result, 'accounts')
  const byKind = dataset(result, 'by_kind')
  const brands = dataset(result, 'brands')
  const changes = dataset(result, 'changes')
  const captures = dataset(result, 'captures')
  const asOf = dataset(result, 'as_of')

  // One company is a real filter (the `company` param), so the drinks table
  // and its export agree. The chips below set it; the server narrows only the
  // drinks, so every other matching account stays here to pick.
  const company = typeof values['company'] === 'string' ? values['company'] : ALL
  const account = company === ALL ? null : Number(company)
  const setAccount = (id: number | null) => {
    setParam('company', id === null ? ALL : String(id))
  }
  // Paging is tied to the payload it was set against, so a new run (a changed
  // filter or search) starts again at the first page.
  const [paging, setPaging] = useState({ of: result, n: PAGE })
  const limit = paging.of === result ? paging.n : PAGE
  const setLimit = (n: number) => {
    setPaging({ of: result, n })
  }

  if (headline === undefined) {
    return <Empty label="This run carried no menu lines" />
  }

  const shown = results
  const chosen = accounts.find((row) => num(row['crm_company_id']) === account)
  const query = text(headline['query']) ?? ''
  const visible = shown.slice(0, limit)
  const truncated = visible.length < shown.length

  return (
    <div className="bespoke-report dm-page">
      <section className="dm-kpis">
        <Kpi
          label="Drinks matched"
          value={whole(num(headline['lines']))}
          detail={query === '(everything)' ? 'no search term' : `for “${query}”`}
        />
        <Kpi
          label="Accounts"
          value={whole(num(headline['accounts']))}
          detail={`${whole(num(headline['existing']))} existing, ${whole(num(headline['prospects']))} prospects`}
        />
        <Kpi
          label="No brand named"
          value={pct(num(headline['unbranded_pct']))}
          detail={`${whole(num(headline['unbranded']))} lines — the open slot`}
        />
        <Kpi
          label="Stores behind them"
          value={whole(num(headline['stores']))}
          detail="One figure per company; cannot be split by market"
        />
        <Kpi
          label="Drawn from"
          value={whole(num(headline['corpus']))}
          detail="Lines captured across all accounts"
        />
      </section>

      <Card
        title="Accounts that matched"
        subtitle="Largest estate first. Pick one to narrow the drinks below; a store count is one figure per company and cannot be split by market."
      >
        {accounts.length === 0 ? (
          <Empty label="Nothing matched. Try a broader term, or widen the category filter." />
        ) : (
          <div className="dm-chips">
            <button
              type="button"
              className={account === null ? 'dm-chip is-on' : 'dm-chip'}
              onClick={() => {
                setAccount(null)
                setLimit(PAGE)
              }}
            >
              All ({whole(num(headline['lines']))})
            </button>
            {accounts.slice(0, 60).map((row) => {
              const id = num(row['crm_company_id'])
              const ours = text(row['crm_status']) === 'existing'
              const stale = text(row['stale']) === 'true'
              return (
                <button
                  key={String(id)}
                  type="button"
                  className={[
                    'dm-chip',
                    account === id ? 'is-on' : '',
                    ours ? 'is-customer' : '',
                    stale ? 'is-stale' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    setAccount(account === id ? null : id)
                    setLimit(PAGE)
                  }}
                  title={
                    stale
                      ? `${text(row['kinds']) ?? ''} · ${staleNote(row)}`
                      : `${text(row['kinds']) ?? ''} · captured ${text(row['captured_on']) ?? ''}`
                  }
                >
                  {text(row['company_name']) ?? NONE}{' '}
                  <span className="dm-chip-n">{count(row['matched'])}</span>
                  {num(row['store_count']) !== null && (
                    <span className="dm-chip-stores">
                      {whole(num(row['store_count']))} stores
                    </span>
                  )}
                  <span className="dm-chip-date">{text(row['captured_on']) ?? NONE}</span>
                  {stale && <StaleTag row={row} />}
                </button>
              )
            })}
          </div>
        )}
      </Card>

      <Card
        title={
          chosen === undefined
            ? 'The drinks'
            : `The drinks — ${text(chosen['company_name']) ?? ''}`
        }
        subtitle={
          truncated
            ? `Showing ${whole(visible.length)} of ${whole(shown.length)} matching lines, largest estate first.`
            : `All ${whole(shown.length)} matching lines, largest estate first.`
        }
        actions={
          <ExportBar
            slug={result.slug}
            params={result.params}
            lines={results.length}
          />
        }
      >
        {shown.length === 0 ? (
          <Empty label="No drinks match." />
        ) : (
          <div className="dm-scroll">
            <table className="dm-table">
              <thead>
                <tr>
                  <th scope="col">Company</th>
                  <th scope="col" className="cell-number">
                    Stores
                  </th>
                  <th scope="col">Drink</th>
                  <th scope="col">Type</th>
                  <th scope="col">Brand</th>
                  <th scope="col">Price</th>
                  <th scope="col">Description</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row, i) => {
                  const unbranded = text(row['unbranded']) === 'true'
                  const existing = text(row['crm_status']) === 'existing'
                  const url = text(row['menu_url'])
                  return (
                    <tr key={`${String(row['crm_company_id'])}-${String(i)}`}>
                      <td>
                        <span className={existing ? 'dm-co is-customer' : 'dm-co'}>
                          {text(row['company_name']) ?? NONE}
                        </span>
                        {text(row['stale']) === 'true' && <StaleTag row={row} />}
                        <span className="dm-where">
                          {text(row['city']) ?? ''}
                          {text(row['state']) === null ? '' : `, ${text(row['state']) ?? ''}`}
                          {` · captured ${text(row['captured_on']) ?? NONE}`}
                        </span>
                      </td>
                      <td className="cell-number">{whole(num(row['store_count']))}</td>
                      <td>
                        {url === null ? (
                          (text(row['item_name']) ?? NONE)
                        ) : (
                          <a className="dm-link" href={url} target="_blank" rel="noreferrer">
                            {text(row['item_name']) ?? NONE}
                          </a>
                        )}
                        {text(row['not_from_concentrate']) === 'true' && (
                          <span className="dm-tag is-nfc">NFC</span>
                        )}
                      </td>
                      <td className="dm-dim">{text(row['kind']) ?? NONE}</td>
                      <td>
                        {unbranded ? (
                          <span className="dm-tag is-open">no brand named</span>
                        ) : (
                          (text(row['brand']) ?? NONE)
                        )}
                      </td>
                      <td className="dm-dim">{text(row['price']) ?? NONE}</td>
                      <td className="dm-wrap dm-dim">{text(row['description']) ?? ''}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {truncated && (
          <div className="dm-more">
            <button
              type="button"
              className="dm-chip"
              onClick={() => {
                setLimit(limit + PAGE)
              }}
            >
              Show {whole(Math.min(PAGE, shown.length - visible.length))} more
            </button>
            <button
              type="button"
              className="dm-chip"
              onClick={() => {
                setLimit(shown.length)
              }}
            >
              Show all {whole(shown.length)}
            </button>
          </div>
        )}
      </Card>

      <div className="dm-columns">
        <Card
          title="Where the open slots are"
          subtitle="A line naming no brand is a supplier the menu does not disclose. Described % explains why an ingredient search reaches smoothies and misses juice — it is a house style, not a capture failure."
        >
          <table className="dm-table">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col" className="cell-number">
                  Lines
                </th>
                <th scope="col" className="cell-number">
                  Accounts
                </th>
                <th scope="col" className="cell-number">
                  No brand
                </th>
                <th scope="col" className="cell-number">
                  Described
                </th>
              </tr>
            </thead>
            <tbody>
              {byKind.map((row) => (
                <tr key={text(row['kind']) ?? ''}>
                  <td>
                    {text(row['kind']) ?? NONE}
                    {text(row['our_category']) === 'true' && (
                      <span className="dm-tag is-ours">ours</span>
                    )}
                  </td>
                  <td className="cell-number">{count(row['lines'])}</td>
                  <td className="cell-number">{count(row['accounts'])}</td>
                  <td className="cell-number dm-open">{pct(num(row['unbranded_pct']))}</td>
                  <td className="cell-number dm-dim">{pct(num(row['described_pct']))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card
          title="Brands printed on these menus"
          subtitle="What a guest can read. This is not the competitor list — it can only ever see a label on a menu, never a manufacturer. See Competitors for that."
        >
          {brands.length === 0 ? (
            <Empty label="No brand is named on any matching line." />
          ) : (
            <div className="dm-scroll dm-short">
              <table className="dm-table">
                <thead>
                  <tr>
                    <th scope="col">Brand</th>
                    <th scope="col" className="cell-number">
                      Accounts
                    </th>
                    <th scope="col" className="cell-number">
                      Lines
                    </th>
                    <th scope="col" className="cell-number">
                      In our categories
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {brands.map((row) => (
                    <tr key={text(row['brand']) ?? ''}>
                      <td>
                        {text(row['brand']) ?? NONE}
                        {text(row['is_ours']) === 'true' && (
                          <span className="dm-tag is-ours">ours</span>
                        )}
                      </td>
                      <td className="cell-number">{count(row['accounts'])}</td>
                      <td className="cell-number">{count(row['lines'])}</td>
                      <td className="cell-number">{count(row['our_category_lines'])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card
        title={`What changed (${String(changes.length)})`}
        subtitle="Adjudicated additions and removals only. The raw diff is 82% the same drink parsed differently and is not used at all. Before 2026-10-02 these compare consecutive days rather than months, so read one as a candidate."
      >
        {changes.length === 0 ? (
          <Empty label="No adjudicated change in this category." />
        ) : (
          <div className="dm-scroll dm-short">
            <table className="dm-table">
              <thead>
                <tr>
                  <th scope="col">Change</th>
                  <th scope="col">Company</th>
                  <th scope="col" className="cell-number">
                    Stores
                  </th>
                  <th scope="col">Drink</th>
                  <th scope="col">Why the model believes it</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((row, i) => {
                  const status = text(row['change_status']) ?? ''
                  return (
                    <tr key={`${text(row['item_name']) ?? ''}-${String(i)}`}>
                      <td>
                        <span
                          className={
                            status === 'new' ? 'dm-tag is-new' : 'dm-tag is-gone'
                          }
                        >
                          {status}
                        </span>
                      </td>
                      <td>{text(row['company_name']) ?? NONE}</td>
                      <td className="cell-number">{whole(num(row['store_count']))}</td>
                      <td>{text(row['item_name']) ?? NONE}</td>
                      <td className="dm-wrap dm-dim">{text(row['change_why']) ?? ''}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="dm-columns">
        <Card
          title="Capture dates behind these lines"
          subtitle="Each account shows its last successful capture, so the date varies by account. A stale account's newest fetch failed and the menu shown is from the date it sits under."
        >
          <table className="dm-table">
            <thead>
              <tr>
                <th scope="col">Captured</th>
                <th scope="col" className="cell-number">
                  Accounts
                </th>
                <th scope="col" className="cell-number">
                  Lines
                </th>
                <th scope="col" className="cell-number">
                  Stale accounts
                </th>
              </tr>
            </thead>
            <tbody>
              {captures.map((row) => (
                <tr key={text(row['captured_on']) ?? ''}>
                  <td>{text(row['captured_on']) ?? NONE}</td>
                  <td className="cell-number">{count(row['accounts'])}</td>
                  <td className="cell-number">{count(row['lines'])}</td>
                  <td className="cell-number dm-dim">
                    {num(row['stale_accounts']) ? whole(num(row['stale_accounts'])) : NONE}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="What this is as of">
          <table className="dm-table">
            <tbody>
              {asOf.map((row) => (
                <tr key={text(row['source']) ?? ''}>
                  <td className="dm-field">{text(row['source'])}</td>
                  <td>
                    <div className="dm-asof-val">{text(row['as_of'])}</div>
                    <div className="dm-note">{text(row['note'])}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  )
}
